import { User } from "firebase/auth";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../hooks/useIsMobile";
import ClubBadge from "../../ClubBadge";
import { getClubBadge } from "../../../data/clubBadges";
import CustomSelect from "../../CustomSelect";
import Modal from "../../Modal";
import ConfirmModal from "../../ConfirmModal";
import { NATIONALITIES } from "../../../data/nationalities";
import {
  fetchCompetitionPlayers, addPlayerToCompetition,
  updateCompetitionPlayer, removeCompetitionPlayer, fetchPlayerUsage,
  adminSearchPlayers,
  AdminCompetition, CompetitionPlayer, PlayerUsage,
} from "../../../api/admin";
import {
  card, primaryBtn, secondaryBtn, dangerBtn, smallBtn,
  inputStyle, selectStyle, tableStyle, thStyle, tdStyle, errorStyle, badge,
} from "../../../styles";

const PAGE_SIZE = 50;

interface Props {
  user: User;
  comp: AdminCompetition;
}

export default function SquadTab({ user, comp }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [players, setPlayers] = useState<CompetitionPlayer[]>([]);
  const [playersTotal, setPlayersTotal] = useState(0);
  const [playersPage, setPlayersPage] = useState(1);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerClubFilter, setPlayerClubFilter] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [updatingPlayerId, setUpdatingPlayerId] = useState<string | null>(null);
  const [changeClubPlayerId, setChangeClubPlayerId] = useState<string | null>(null);
  const [changeClubValue, setChangeClubValue] = useState("");
  const [error, setError] = useState("");
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [addPlayerModal, setAddPlayerModal] = useState(false);
  const [addPlayerMode, setAddPlayerMode] = useState<"existing" | "new">("existing");
  const [addPlayerClubId, setAddPlayerClubId] = useState("");
  const [addPlayerName, setAddPlayerName] = useState("");
  const [addPlayerPosition, setAddPlayerPosition] = useState("");
  const [addPlayerNationality, setAddPlayerNationality] = useState("");
  const [addPlayerSearch, setAddPlayerSearch] = useState("");
  const [addPlayerResults, setAddPlayerResults] = useState<{ id: string; name: string; club: string }[]>([]);
  const [selectedExistingPlayer, setSelectedExistingPlayer] = useState<{ id: string; name: string } | null>(null);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [addPlayerError, setAddPlayerError] = useState("");

  const loadPlayers = async (opts?: { search?: string; clubId?: string; active?: boolean; page?: number }) => {
    setPlayersLoading(true);
    try {
      const params: Parameters<typeof fetchCompetitionPlayers>[2] = {};
      if (opts?.search) params.search = opts.search;
      if (opts?.clubId) params.clubId = opts.clubId;
      if (opts?.active !== undefined) params.active = opts.active;
      params.page = opts?.page ?? 1;
      const data = await fetchCompetitionPlayers(user, comp.id, params);
      setPlayers(data.players);
      setPlayersTotal(data.total);
    } catch {
      setError(t("admin.errors.failedToLoad"));
    } finally {
      setPlayersLoading(false);
    }
  };

  useEffect(() => {
    if (playerClubFilter || playerSearch.length >= 3) {
      loadPlayers({ active: showAll ? undefined : true, clubId: playerClubFilter || undefined, search: playerSearch || undefined, page: playersPage });
    }
  }, [playersPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerPlayerSearch = (search: string, clubId: string, all: boolean) => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (search.length > 0 && search.length < 3) return;
    searchDebounce.current = setTimeout(() => {
      setPlayersPage(1);
      if (!search && !clubId) {
        setPlayers([]);
        setPlayersTotal(0);
        return;
      }
      loadPlayers({ search: search || undefined, clubId: clubId || undefined, active: all ? undefined : true, page: 1 });
    }, 300);
  };

  const handleToggleActive = async (playerId: string, currentlyActive: boolean) => {
    setUpdatingPlayerId(playerId);
    try {
      const updated = await updateCompetitionPlayer(user, comp.id, playerId, { active: !currentlyActive });
      setPlayers((prev) => prev.map((p) => (p.playerId === playerId ? updated : p)));
    } catch {
      setError("Failed to update player");
    } finally {
      setUpdatingPlayerId(null);
    }
  };

  const handleChangeClub = async (playerId: string) => {
    if (!changeClubValue) return;
    setUpdatingPlayerId(playerId);
    try {
      const updated = await updateCompetitionPlayer(user, comp.id, playerId, { clubId: changeClubValue });
      setPlayers((prev) => prev.map((p) => (p.playerId === playerId ? updated : p)));
      setChangeClubPlayerId(null); setChangeClubValue("");
    } catch {
      setError("Failed to change club");
    } finally {
      setUpdatingPlayerId(null);
    }
  };

  const [confirmRemovePlayer, setConfirmRemovePlayer] = useState<{ playerId: string; name: string } | null>(null);
  const [removeUsage, setRemoveUsage] = useState<PlayerUsage | null>(null);
  const [removeUsageLoading, setRemoveUsageLoading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const openRemoveConfirm = async (playerId: string, name: string) => {
    setConfirmRemovePlayer({ playerId, name });
    setRemoveUsage(null);
    setRemoveUsageLoading(true);
    try {
      setRemoveUsage(await fetchPlayerUsage(user, comp.id, playerId));
    } catch {
      setRemoveUsage({ selections: 0, stats: 0, gwPicks: 0, syndicatePicks: 0, total: 0 });
    } finally {
      setRemoveUsageLoading(false);
    }
  };

  const handleRemovePlayer = async () => {
    if (!confirmRemovePlayer) return;
    const { playerId } = confirmRemovePlayer;
    setRemoving(true);
    try {
      await removeCompetitionPlayer(user, comp.id, playerId);
      setConfirmRemovePlayer(null);
      loadPlayers({ search: playerSearch || undefined, clubId: playerClubFilter || undefined, active: showAll ? undefined : true, page: playersPage });
    } catch {
      setError("Failed to remove player");
      setConfirmRemovePlayer(null);
    } finally {
      setRemoving(false);
    }
  };

  const resetAddPlayerModal = () => {
    setAddPlayerMode("existing"); setAddPlayerClubId(""); setAddPlayerName("");
    setAddPlayerPosition(""); setAddPlayerNationality(""); setAddPlayerSearch("");
    setAddPlayerResults([]); setSelectedExistingPlayer(null); setAddPlayerError("");
  };

  const handleAddPlayer = async () => {
    if (!addPlayerClubId) { setAddPlayerError("Select a club"); return; }
    if (addPlayerMode === "existing" && !selectedExistingPlayer) { setAddPlayerError("Select a player"); return; }
    if (addPlayerMode === "new" && !addPlayerName.trim()) { setAddPlayerError("Name is required"); return; }
    setAddingPlayer(true); setAddPlayerError("");
    try {
      await addPlayerToCompetition(user, comp.id, {
        ...(addPlayerMode === "existing"
          ? { playerId: selectedExistingPlayer!.id }
          : { name: addPlayerName.trim(), position: addPlayerPosition.trim() || undefined, nationality: addPlayerNationality.trim() || undefined }),
        clubId: addPlayerClubId,
      });
      setAddPlayerModal(false); resetAddPlayerModal();
      setPlayersPage(1);
      loadPlayers({ search: playerSearch || undefined, clubId: playerClubFilter || undefined, active: showAll ? undefined : true, page: 1 });
    } catch {
      setAddPlayerError("Failed to add player. They may already be in this competition.");
    } finally {
      setAddingPlayer(false);
    }
  };

  return (
    <div style={card}>
      {error && <p style={{ ...errorStyle, marginBottom: "1rem" }}>{error}</p>}
      {/* Filters + Add button */}
      <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px" }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "var(--color-text-muted)", marginBottom: "0.2rem" }}>Search</label>
          <input
            style={inputStyle}
            placeholder="Player name…"
            value={playerSearch}
            onChange={(e) => {
              setPlayerSearch(e.target.value);
              triggerPlayerSearch(e.target.value, playerClubFilter, showAll);
            }}
          />
        </div>
        <div style={{ flex: "1 1 160px" }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "var(--color-text-muted)", marginBottom: "0.2rem" }}>Team</label>
          <CustomSelect
            value={playerClubFilter}
            onChange={(v) => {
              setPlayerClubFilter(v);
              setPlayersPage(1);
              loadPlayers({ search: playerSearch || undefined, clubId: v || undefined, active: showAll ? undefined : true, page: 1 });
            }}
            placeholder="— All teams —"
            options={comp.clubs.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)", cursor: "pointer", paddingBottom: "0.15rem" }}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => {
              setShowAll(e.target.checked);
              setPlayersPage(1);
              loadPlayers({ search: playerSearch || undefined, clubId: playerClubFilter || undefined, active: e.target.checked ? undefined : true, page: 1 });
            }}
          />
          Show transferred out
        </label>
        <button style={{ ...primaryBtn, flexShrink: 0 }} onClick={() => { resetAddPlayerModal(); setAddPlayerModal(true); }}>
          + Add Player
        </button>
      </div>

      {/* Player list grouped by club */}
      {playersLoading ? (
        <p style={{ color: "var(--color-text-muted)", margin: 0 }}>Loading…</p>
      ) : players.length === 0 ? (
        <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
          {playerClubFilter || playerSearch.length >= 3 ? "No players match your filters." : "Select a team or search (3+ characters) to view players."}
        </p>
      ) : (() => {
        const POS_ORDER: Record<string, number> = { GK: 0, DF: 1, MF: 2, FW: 3 };
        const clubsToShow = playerClubFilter
          ? comp.clubs.filter((c) => c.id === playerClubFilter)
          : comp.clubs;
        const groups = clubsToShow.map((club) => ({
          club,
          clubPlayers: players
            .filter((p) => p.club.id === club.id)
            .sort((a, b) => {
              const pa = POS_ORDER[a.position ?? ""] ?? 99;
              const pb = POS_ORDER[b.position ?? ""] ?? 99;
              return pa !== pb ? pa - pb : a.name.localeCompare(b.name);
            }),
        })).filter((g) => g.clubPlayers.length > 0);

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {groups.map(({ club, clubPlayers }) => (
              <div key={club.id}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", paddingBottom: "0.4rem", borderBottom: "2px solid var(--color-border-subtle)" }}>
                  <ClubBadge config={getClubBadge(club.name)} size={20} title={club.name} />
                  <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--color-text)" }}>{club.name}</span>
                  <span style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>({clubPlayers.length})</span>
                </div>
                <table style={{ ...tableStyle, tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: isMobile ? "50%" : "38%" }} />
                    {!isMobile && <col style={{ width: "10%" }} />}
                    <col style={{ width: isMobile ? "20%" : "18%" }} />
                    <col style={{ width: isMobile ? "30%" : "34%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thStyle}>Name</th>
                      {!isMobile && <th style={thStyle}>Pos</th>}
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubPlayers.map((p) => {
                      const isUpdating = updatingPlayerId === p.playerId;
                      const isChangingClub = changeClubPlayerId === p.playerId;
                      return (
                        <tr key={p.playerId} style={{ opacity: isUpdating ? 0.5 : 1 }}>
                          <td style={{ ...tdStyle, fontWeight: 500 }}>
                            {p.name}
                            {isChangingClub && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", marginLeft: "0.5rem" }}>
                                <CustomSelect
                                  value={changeClubValue}
                                  onChange={setChangeClubValue}
                                  placeholder="— Team —"
                                  options={comp.clubs.map((c) => ({ value: c.id, label: c.name }))}
                                />
                                <button style={{ ...smallBtn, fontSize: "0.72rem", padding: "0.2rem 0.4rem" }} onClick={() => handleChangeClub(p.playerId)} disabled={!changeClubValue || isUpdating}>Save</button>
                                <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-dim)", fontSize: "0.85rem" }} onClick={() => { setChangeClubPlayerId(null); setChangeClubValue(""); }}>✕</button>
                              </span>
                            )}
                          </td>
                          {!isMobile && <td style={{ ...tdStyle, color: "var(--color-text-dim)", fontSize: "0.82rem" }}>{p.position ?? "—"}</td>}
                          <td style={tdStyle}>
                            {p.active
                              ? <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)", fontSize: "0.7rem" }}>Active</span>
                              : <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", fontSize: "0.7rem" }}>Transferred out</span>}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                            <button
                              style={{ ...smallBtn, fontSize: "0.75rem", marginRight: "0.3rem" }}
                              disabled={isUpdating}
                              onClick={() => {
                                if (isChangingClub) { setChangeClubPlayerId(null); setChangeClubValue(""); }
                                else handleToggleActive(p.playerId, p.active);
                              }}
                            >
                              {isChangingClub ? "Cancel" : p.active ? "Transfer Out" : "Reactivate"}
                            </button>
                            {!isChangingClub && (
                              <button style={{ ...smallBtn, fontSize: "0.75rem", marginRight: "0.3rem" }} disabled={isUpdating} onClick={() => { setChangeClubPlayerId(p.playerId); setChangeClubValue(p.club.id); }}>
                                Move
                              </button>
                            )}
                            <button style={{ ...dangerBtn, fontSize: "0.75rem" }} disabled={isUpdating} onClick={() => openRemoveConfirm(p.playerId, p.name)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
            <p style={{ fontSize: "0.78rem", color: "var(--color-text-dim)", margin: 0 }}>
              {playersTotal} player{playersTotal !== 1 ? "s" : ""} total
              {!showAll && " · active only"}
              {playerClubFilter && ` · ${comp.clubs.find((c) => c.id === playerClubFilter)?.name ?? ""}`}
            </p>
          </div>
        );
      })()}
      {Math.ceil(playersTotal / PAGE_SIZE) > 1 && (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.75rem 0 0" }}>
          <button style={smallBtn} disabled={playersPage <= 1} onClick={() => setPlayersPage((p) => p - 1)}>
            Previous
          </button>
          <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
            Page {playersPage} of {Math.ceil(playersTotal / PAGE_SIZE)}
          </span>
          <button style={smallBtn} disabled={playersPage >= Math.ceil(playersTotal / PAGE_SIZE)} onClick={() => setPlayersPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {/* Add player modal */}
      {addPlayerModal && (
        <Modal onClose={() => { setAddPlayerModal(false); resetAddPlayerModal(); }} blur={false}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)", padding: "1.5rem", maxWidth: "440px", width: "90%", maxHeight: "85vh", overflowY: "auto" }}>
            <h3 style={{ marginTop: 0 }}>Add Player — {comp.name}</h3>
            {addPlayerError && <p style={errorStyle}>{addPlayerError}</p>}

            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
              <button style={addPlayerMode === "existing" ? primaryBtn : secondaryBtn} onClick={() => setAddPlayerMode("existing")}>Existing Player</button>
              <button style={addPlayerMode === "new" ? primaryBtn : secondaryBtn} onClick={() => setAddPlayerMode("new")}>New Player</button>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>Team *</label>
              <CustomSelect
                value={addPlayerClubId}
                onChange={setAddPlayerClubId}
                placeholder="— Select team —"
                options={comp.clubs.map((c) => ({ value: c.id, label: c.name }))}
              />
            </div>

            {addPlayerMode === "existing" ? (
              <div style={{ marginBottom: "0.75rem" }}>
                <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>Search Player *</label>
                <div style={{ position: "relative" }}>
                  <input
                    style={inputStyle}
                    placeholder="Type player name…"
                    value={addPlayerSearch}
                    onChange={(e) => {
                      setAddPlayerSearch(e.target.value);
                      setSelectedExistingPlayer(null);
                      if (e.target.value.trim()) {
                        adminSearchPlayers(user, e.target.value).then((r) => setAddPlayerResults(r.players)).catch(() => {});
                      } else {
                        setAddPlayerResults([]);
                      }
                    }}
                  />
                  {addPlayerResults.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "6px", zIndex: 10, maxHeight: "200px", overflowY: "auto" }}>
                      {addPlayerResults.map((p) => (
                        <div
                          key={p.id}
                          style={{ padding: "0.4rem 0.75rem", cursor: "pointer", fontSize: "0.9rem", color: "var(--color-text)" }}
                          onClick={() => { setSelectedExistingPlayer({ id: p.id, name: p.name }); setAddPlayerSearch(p.name); setAddPlayerResults([]); }}
                        >
                          {p.name}{p.club ? ` — ${p.club}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedExistingPlayer && (
                  <p style={{ fontSize: "0.82rem", color: "var(--color-success)", marginTop: "0.3rem", marginBottom: 0 }}>✓ {selectedExistingPlayer.name}</p>
                )}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "0.75rem" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>Name *</label>
                  <input style={inputStyle} value={addPlayerName} onChange={(e) => setAddPlayerName(e.target.value)} />
                </div>
                <div style={{ marginBottom: "0.75rem" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>Position</label>
                  <select style={selectStyle} value={addPlayerPosition} onChange={(e) => setAddPlayerPosition(e.target.value)}>
                    <option value="">— Select —</option>
                    <option value="GK">GK — Goalkeeper</option>
                    <option value="DF">DF — Defender</option>
                    <option value="MF">MF — Midfielder</option>
                    <option value="FW">FW — Forward</option>
                  </select>
                </div>
                <div style={{ marginBottom: "0.75rem" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>Nationality</label>
                  <select style={selectStyle} value={addPlayerNationality} onChange={(e) => setAddPlayerNationality(e.target.value)}>
                    <option value="">— None —</option>
                    {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button style={secondaryBtn} onClick={() => { setAddPlayerModal(false); resetAddPlayerModal(); }}>Cancel</button>
              <button style={primaryBtn} onClick={handleAddPlayer} disabled={addingPlayer}>
                {addingPlayer ? "Adding…" : "Add to Squad"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {confirmRemovePlayer && (
        removeUsageLoading || !removeUsage ? (
          <ConfirmModal
            title="Remove player?"
            message="Checking for selection/pick history…"
            confirmLabel="OK"
            confirming
            onConfirm={() => {}}
            onCancel={() => setConfirmRemovePlayer(null)}
          />
        ) : removeUsage.total > 0 ? (
          <ConfirmModal
            title="Cannot permanently delete this player"
            message={`${confirmRemovePlayer.name} has selection/pick history (${removeUsage.selections} selection(s), ${removeUsage.stats} stat row(s), ${removeUsage.gwPicks} GW pick(s), ${removeUsage.syndicatePicks} syndicate pick(s)) and cannot be hard-deleted. Use Transfer Out instead.`}
            confirmLabel="OK"
            danger={false}
            onConfirm={() => setConfirmRemovePlayer(null)}
            onCancel={() => setConfirmRemovePlayer(null)}
          />
        ) : (
          <ConfirmModal
            title="Remove player?"
            message={`Permanently remove ${confirmRemovePlayer.name} from this competition? This is only for players added by mistake — cannot be undone.`}
            confirmLabel="Remove"
            confirming={removing}
            onConfirm={handleRemovePlayer}
            onCancel={() => setConfirmRemovePlayer(null)}
          />
        )
      )}
    </div>
  );
}
